import { createAction } from 'redux-actions';
import {
    API_EVERYDAY_NOTE,
    API_CONTROL_EVERYDAY_NOTE,
} from 'constants/endpoints';
import { adminActionTypeCreator, Meta, metaCreator, Payload } from '../../utils';
import { StatusEnum } from '../../StatusEnum';

const createActionType = adminActionTypeCreator;


export const search = createAction(
    createActionType(API_EVERYDAY_NOTE, 'search'),
    (data, page) => {
        const pageNum = page === undefined ? '1' : page;
        return {
            method: 'get',
            api: `${API_EVERYDAY_NOTE}/${pageNum}`,
            data,
        }
    },
    metaCreator
);

export const create = createAction(
    createActionType(API_EVERYDAY_NOTE, 'create'),
    (data) => ({
        method: 'put',
        api: `${API_EVERYDAY_NOTE}/create`,
        data,
    }),
    metaCreator
);

export const update = createAction(
    createActionType(API_EVERYDAY_NOTE, 'update'),
    (data) => {
        const { uuid, ...restData } = data;
        return {
            method: 'post',
            api: `${API_EVERYDAY_NOTE}/update/${uuid}`,
            data: restData,
        }
    },
    metaCreator
);

const deleteAction = createAction(
    createActionType(API_EVERYDAY_NOTE, 'delete'),
    (uuid) => ({
        method: 'delete',
        api: `${API_EVERYDAY_NOTE}/delete/${uuid}`,
    }),
    metaCreator
);
export { deleteAction as delete };

export const resultCsv = createAction(
    createActionType(API_EVERYDAY_NOTE, 'result-csv'),
    (questionnaireUuid, data) => ({
        method: 'get',
        api: `${API_EVERYDAY_NOTE}/result-csv/${questionnaireUuid}`,
        data,
    }),
    (questionnaireUuid) => ({
        fetch: true,
        status: StatusEnum.REQUEST,
        uuid: questionnaireUuid,
    })
);

type SearchParameter = Http.Connection.Request.Parameter.EverydayNote.ResultTotal;
export const resultTotal = createAction<
    Payload<{ data: SearchParameter }> | Http.Connection.Response.EverydayNote.ResultTotal,
    Meta,
    string,
    string,
    SearchParameter
>(
    createActionType(API_EVERYDAY_NOTE, 'result-total'),
    (questionnaireUuid, itemUuid, data) => ({
        method: 'get',
        api: `${API_EVERYDAY_NOTE}/${questionnaireUuid}/${itemUuid}/result-total`,
        data,
    }),
    metaCreator
);

export const resultSubItems = createAction<
    Payload<{ data: SearchParameter }> | Http.Connection.Response.EverydayNote.ResultSubItems,
    Meta,
    string,
    string,
    SearchParameter,
    number
>(
    createActionType(API_EVERYDAY_NOTE, 'result-sub-items'),
    (questionnaireUuid, itemUuid, data, page) => ({
        method: 'get',
        api: `${API_EVERYDAY_NOTE}/${questionnaireUuid}/${itemUuid}/${page}/result-sub-items`,
        data,
    }),
    metaCreator
);

export const resultSubTotal = createAction<
    Payload<{ data: SearchParameter }> | Http.Connection.Response.EverydayNote.ResultSubTotal,
    Meta,
    string,
    string,
    SearchParameter,
    number
>(
    createActionType(API_EVERYDAY_NOTE, 'result-sub-total'),
    (questionnaireUuid, itemUuid, data, page) => ({
        method: 'get',
        api: `${API_EVERYDAY_NOTE}/${questionnaireUuid}/${itemUuid}/${page}/result-sub-total`,
        data,
    }),
    metaCreator
);

export const view = createAction(
    createActionType(API_EVERYDAY_NOTE, 'view' as const),
    (uuid) => ({
        method: 'get',
        api: `${API_EVERYDAY_NOTE}/view/${uuid}`,
    }),
    metaCreator
);

export const controlResultCsv = createAction(
    createActionType(API_CONTROL_EVERYDAY_NOTE, 'result-csv'),
    (questionnaireUuid, data) => ({
        method: 'get',
        api: `${API_CONTROL_EVERYDAY_NOTE}/result-csv/${questionnaireUuid}`,
        data,
    }),
    (questionnaireUuid) => ({
        fetch: true,
        status: StatusEnum.REQUEST,
        uuid: questionnaireUuid,
    })
);

export const controlResultTotal = createAction(
    createActionType(API_CONTROL_EVERYDAY_NOTE, 'result-total'),
    (questionnaireUuid, itemUuid, data) => ({
        method: 'get',
        api: `${API_CONTROL_EVERYDAY_NOTE}/${questionnaireUuid}/${itemUuid}/result-total`,
        data,
    }),
    metaCreator
);

export const controlResultSubItems = createAction<
    Payload<{ data: SearchParameter }> | Http.Connection.Response.EverydayNote.ResultSubItems,
    Meta,
    string,
    string,
    SearchParameter,
    number
>(
    createActionType(API_CONTROL_EVERYDAY_NOTE, 'result-sub-items'),
    (questionnaireUuid, itemUuid, data, page) => ({
        method: 'get',
        api: `${API_CONTROL_EVERYDAY_NOTE}/${questionnaireUuid}/${itemUuid}/${page}/result-sub-items`,
        data,
    }),
    metaCreator
);

export const controlResultSubTotal = createAction<
    Payload<{ data: SearchParameter }> | Http.Connection.Response.EverydayNote.ResultSubTotal,
    Meta,
    string,
    string,
    SearchParameter,
    number
>(
    createActionType(API_CONTROL_EVERYDAY_NOTE, 'result-sub-total'),
    (questionnaireUuid, itemUuid, data, page) => ({
        method: 'get',
        api: `${API_CONTROL_EVERYDAY_NOTE}/${questionnaireUuid}/${itemUuid}/${page}/result-sub-total`,
        data,
    }),
    metaCreator
);

export const controlSearch = createAction(
    createActionType(API_CONTROL_EVERYDAY_NOTE, 'search'),
    (data, page) => {
        const pageNum = page === undefined ? '1' : page;
        return {
            method: 'get',
            api: `${API_CONTROL_EVERYDAY_NOTE}/${pageNum}`,
            data,
        }
    },
    metaCreator
);

export const controlUpdate = createAction(
    createActionType(API_CONTROL_EVERYDAY_NOTE, 'update'),
    (data) => {
        const { uuid, ...restData } = data;
        return {
            method: 'post',
            api: `${API_CONTROL_EVERYDAY_NOTE}/update/${uuid}`,
            data: restData,
        }
    },
    metaCreator
);

export const controlView = createAction(
    createActionType(API_CONTROL_EVERYDAY_NOTE, 'view'),
    (uuid) => ({
        method: 'get',
        api: `${API_CONTROL_EVERYDAY_NOTE}/view/${uuid}`,
    }),
    metaCreator
);

export const controlTargetSchoolClass = createAction(
    createActionType(API_CONTROL_EVERYDAY_NOTE, 'target-school-class'),
    (uuid) => ({
        api: `${API_CONTROL_EVERYDAY_NOTE}/target-school-class/${uuid}`,
        method: 'get',
    }),
    metaCreator
);

export const targetSchoolClass = createAction(
    createActionType(API_EVERYDAY_NOTE, 'target-school-class'),
    (uuid) => ({
        api: `${API_EVERYDAY_NOTE}/target-school-class/${uuid}`,
        method: 'get',
    }),
    metaCreator
);